import React, { useCallback, useEffect } from 'react'
import { useConnectPanel } from 'contexts/ConnectPanel'
import useAuth from 'hooks/useAuth'
import { useWeb3React } from 'lib/wagmi-compat/useWeb3React'
import { useAwaitingPath, useIsUserVerified, useUserData } from 'state/user/hooks'
import connectors from 'config/walletConnect/config'
import { ConnectVerifyPanel } from 'components/widgets/ConnectVerifyPanel'
import { useSetupNetwork } from 'hooks/useSetupNetwork'
import { useAutoNetworkSwitching } from 'hooks/useAutoNetworkSwitching'
import { ExternalProvider } from '@ethersproject/providers'
import { waitForAccount } from 'utils/wallet'
import { useRouter } from 'hooks/useRouter'
import { OPEN_WALLET_PANEL_QUERY } from 'constants/site'
import { isSupportedChainId } from 'connectors/utils'
import { metamaskMobileStore } from 'mobxStores/MetamaskMobile'
import { useVerify } from 'state/user/hooks/useVerifyUser'
import { getChainIdNumber } from 'utils/getChainIdNumber'
import Header from '../components/Header'
import Footer from '../components/Footer'

interface LayoutProps {
  children: React.ReactNode
}

const AppLayout: React.FC<LayoutProps> = ({ children }) => {
  const { query, replace, pathname } = useRouter()
  const { connectPanelOpen, connectPanelDisabled, setConnectPanelOpen } = useConnectPanel()
  const { login, logout } = useAuth()
  const { account, connector, isActive, chainId, provider } = useWeb3React()
  /**
   * Whether the user is verified with the platform, which is not the same as
   * having a wallet connected.
   *
   * The wallet panel used to be handed `isActive` — wagmi's "a wallet is
   * attached" — so it showed the verified badge to anyone who had merely
   * connected, while the header showed the truth. One of the two had to be
   * lying, and it was the panel; worse, believing the user verified is what
   * hid the way to actually verify.
   */
  const isUserVerifiedRedux = useIsUserVerified()
  const userData = useUserData()
  const isUserVerified = !!(isUserVerifiedRedux && userData?.userData?.userId)
  const { setupNetwork } = useSetupNetwork()
  const { updateAwaitingPath, awaitingPath } = useAwaitingPath()
  const { verifyUser } = useVerify()

  const closeHandlerWalletExist = async (activeConnector: any) => {
    if (activeConnector?.deactivate) {
      await activeConnector.deactivate()
    } else if (activeConnector?.resetState) {
      await activeConnector.resetState()
    }
  }

  const handlePanelBlur = () => {
    if (awaitingPath) updateAwaitingPath('')
    setConnectPanelOpen(false)
  }

  const fixNetwork = useCallback(
    async (targetChainId: number, customProvider: ExternalProvider, active: boolean) => {
      const activeProvider = customProvider || (window as any).ethereum || (window as any).trustwallet
      const currentChainIdRaw = await activeProvider?.request?.({
        method: 'eth_chainId',
        params: []
      })
      const currentChainId = getChainIdNumber(currentChainIdRaw)

      if (targetChainId === currentChainId) return
      if (!currentChainId) return
      if (!active) return

      if (!isSupportedChainId(targetChainId)) {
        closeHandlerWalletExist(connector)
        return
      }

      await activeProvider.request({
        method: 'wallet_switchEthereumChain',
        params: [{ chainId: `0x${targetChainId.toString(16)}` }]
      })
      await waitForAccount(activeProvider)
    },
    [connector]
  )

  useEffect(() => {
    if (!chainId) return
    void fixNetwork(chainId, provider?.provider, isActive)
  }, [chainId, provider?.provider, isActive, fixNetwork])

  useEffect(() => {
    // @ts-ignore
    if (connector?.provider?.session?.peer?.metadata?.redirect?.native === 'metamask://') {
      if (account && chainId && connector) {
        void connector.activate?.(chainId)
        metamaskMobileStore.setLoading(false)
      } else {
        closeHandlerWalletExist(connector)
      }
    }
  }, [connector, account])

  useAutoNetworkSwitching()

  const openConnectParam = query[OPEN_WALLET_PANEL_QUERY]

  useEffect(() => {
    if (openConnectParam !== '1' && openConnectParam !== true) return
    if (!connectPanelDisabled) setConnectPanelOpen(true)
    const nextQuery = { ...query }
    delete nextQuery[OPEN_WALLET_PANEL_QUERY]
    replace({ pathname, query: nextQuery })
  }, [openConnectParam, connectPanelDisabled, pathname, query, replace, setConnectPanelOpen])

  return (
    <>
      <Header />
      <ConnectVerifyPanel
        handleLogin={(args) => {
          login({
            ...args,
            onSuccessCallback: async (connectedAccount, usedConnector) => {
              await verifyUser({
                customAccount: connectedAccount,
                customProvider: usedConnector,
                onErrorCallback: async () => {
                  if (usedConnector) {
                    await logout({ connector: usedConnector, autoDisconnect: true })
                  }
                },
              })
              setConnectPanelOpen(false)
            },
          })
        }}
        isConnectWalletPanelOpen={connectPanelOpen}
        setIsConnectWalletPanelOpen={(open) => {
          if (!connectPanelDisabled) setConnectPanelOpen(open)
        }}
        logout={() => {
          logout({ connector })
        }}
        isUserVerified={isUserVerified}
        onVerifyUser={async () => {}}
        account={account}
        isActive={isActive}
        handlePanelBlur={handlePanelBlur}
        connectors={connectors}
        chainId={chainId}
        setupNetwork={setupNetwork}
      />
      {children}
      <Footer />
    </>
  )
}

export default AppLayout
